import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Formik, FormikHelpers, FormikProps } from "formik";
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  SubmitButton,
} from "formik-antd";
import { simpleSchemaBuilder } from "/app/src/helpers";
import Box from "/app/src/components/generic/components/box";
import { Accordion, AccordionItem } from "@heroui/react";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { CostCenter, iField } from "/app/src/models";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import HeroButton from "/app/src/components/HeroUi/Button";
import { Moment } from "moment";
import { useQuery } from "@tanstack/react-query";
import { costCenterService } from "/app/src/services";
import { SearchFilter } from "/app/src/components/generic/components/searchFilter";
import DebouncedMaterialSelect from "../debouncedMaterialSelect";

interface OrderLineType {
  id?: number;
  materialName: string;
  quantity: number;
  allocate?: string;
  costCenterId?: string;
  hostIdentification?: string;
  releaseDate?: string;
  qualification?: string;
  productionDate?: string;
  expiryDate?: string;
  serialNumber?: string;
  Info1?: string;
  Info2?: string;
  Info3?: string;
  Info4?: string;
  Info5?: string;
}

/**
 * Renders a line component with a dynamic Formik form.
 *
 * @param {Object} props - The component properties.
 * @param {any} props.line - The order builder line object.
 * @param {(lineId: number) => void} props.removeLine - Callback function to remove the line.
 * @param {(lineId: number, newQuantity: number) => void} props.updateLineQuantity - Callback function to update the line quantity.
 * @param {OrderLineField[]} props.orderLineFields - Array of order line field definitions.
 * @param {(lineId: number, newLine: any) => void} props.updateLine - Callback function to update the line.
 * @returns {JSX.Element} The rendered line component.
 */
export default function Line({
  line,
  removeLine,
  orderLineFields,
  updateLine,
}: {
  line: OrderLineType;
  removeLine: (lineId: number) => void;
  orderLineFields: iField[];
  updateLine: (lineId: number, newLine: OrderLineType) => void;
}) {
  const { t } = useTranslation();

  /**
   * Toggles the indicator icon based on open state.
   * @param {object} param0 - The object containing the open state.
   * @param {boolean} param0.isOpen - Indicates if the accordion is open.
   * @returns {JSX.Element} The indicator icon component element.
   */
  const toggleIndicatorIcon = useCallback(({ isOpen }) => {
    return isOpen ? (
      <IconBuilder icon="ChevronRight" />
    ) : (
      <IconBuilder icon="ChevronDown" />
    );
  }, []);

  const { data: costCenters } = useQuery({
    queryKey: ["costCenters"],
    queryFn: () => {
      return costCenterService.getAll();
    },
    initialData: { cost_centers: [] },
    select: (data: { cost_centers: CostCenter[] }) => data.cost_centers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled:
      orderLineFields.find((field) => field.name === "costCenterId") !==
      undefined,
  });

  const handleDeleteClick = useCallback((): void => {
    if (line.id) {
      removeLine(line.id);
    }
  }, [line, removeLine]);

  const orderLineTitle = useCallback(() => {
    return (
      <div className="flex items-center">
        <span className="text-[18px] w-[500px]">
          {t("translation:material_name")}: {line.materialName}
        </span>
        <span className="text-[18px] w-[500px]">
          {t("translation:quantity")}: {line.quantity}
        </span>
      </div>
    );
  }, [line.materialName, line.quantity, t]);

  // Initialize form values based on line keys
  const initialValues = useCallback(() => {
    const values: OrderLineType = {
      materialName: line.materialName || "",
      quantity: line.quantity || 0,
    };
    orderLineFields.forEach((field) => {
      values[field.name] = line[field.name] || "";
    });
    return values;
  }, [line, orderLineFields]);

  // Handle form submission
  const handleSubmit = useCallback(
    (values: OrderLineType, actions: FormikHelpers<OrderLineType>) => {
      if (line.id) {
        updateLine(line.id, { ...line, ...values });
        actions.setSubmitting(false);
        actions.resetForm();
      }
    },
    [line, updateLine],
  );

  const mapCostCenters = useCallback((costCenter: CostCenter) => {
    return (
      <Select.Option key={costCenter.id} value={costCenter.id}>
        {costCenter.name}
      </Select.Option>
    );
  }, []);

  const createDynamicOrderLineFieldsForm = useCallback(
    (values: OrderLineType, setFieldValue) => {
      // Dynamically create onChangeDatePicker function for each field
      const getOnChangeDatePicker = (fieldName: string) => (value: Moment) => {
        setFieldValue(fieldName, value);
      };

      /**
       * Get the display value for a given field name or return a default label.
       * @param {string} fieldName - The name of the field to find.
       * @param {string} defaultLabel - The default label if field not found.
       * @returns {any} The values of the field or the default label.
       */
      const getNameFieldValue = (fieldName: string, defaultLabel) => {
        const field = orderLineFields.find((f) => f.name === fieldName);
        return field ? field.values : defaultLabel;
      };
      return (
        <>
          <Form.Item
            name="materialName"
            hasFeedback={false}
            label={getNameFieldValue(
              "materialName",
              t("translation:material_name"),
            )}
          >
            <DebouncedMaterialSelect
              name="materialName"
              placeholder={t("translation:select_material")}
              setFieldValue={setFieldValue}
            />
          </Form.Item>
          <Form.Item
            name="quantity"
            hasFeedback={false}
            label={t("translation:quantity")}
          >
            <InputNumber
              style={{ width: "100%" }} // Ensure the input takes full width
              name="quantity"
              size="large"
            />
          </Form.Item>
          {orderLineFields
            .filter((field) => field.name !== "materialName")
            .map((field) => (
              <Form.Item
                name={field.name}
                key={field.name}
                hasFeedback={false}
                label={field.values}
              >
                {["expiryDate", "productionDate", "releaseDate"].includes(
                  field.name,
                ) ? (
                  <DatePicker
                    name={field.name}
                    showTime
                    onChange={getOnChangeDatePicker(field.name)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                ) : field.name === "costCenterId" ? (
                  <SearchFilter
                    name={field.name}
                    mapOptionsFn={mapCostCenters}
                    list={costCenters}
                  />
                ) : (
                  <Input name={field.name} size="large" />
                )}
              </Form.Item>
            ))}
        </>
      );
    },
    [costCenters, mapCostCenters, orderLineFields, t],
  );

  const createLineForm: (props: FormikProps<OrderLineType>) => JSX.Element =
    useCallback(
      ({ dirty, isValid, setFieldValue, values }) => (
        <Form layout="vertical">
          <div className="flex justify-end items-center gap-2">
            <SubmitButton type="primary" disabled={!dirty || !isValid}>
              {t("translation:save")}
            </SubmitButton>
            <HeroButton
              onClick={handleDeleteClick}
              color="default"
              size="sm"
              variant="bordered"
              isIconOnly
              className="border-error-default bg-white"
            >
              <IconBuilder size={18} color="#e00000" icon="Delete" />
            </HeroButton>
          </div>
          <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
            {createDynamicOrderLineFieldsForm(values, setFieldValue)}
          </GridWrapper>
        </Form>
      ),
      [createDynamicOrderLineFieldsForm, handleDeleteClick, t],
    );

  return (
    <Box className="!mb-[1px]">
      <Accordion
        itemClasses={{
          trigger: "py-0",
        }}
        selectionMode="single"
        showDivider
      >
        <AccordionItem
          classNames={{
            title: "text-[18px]",
          }}
          key="1"
          title={orderLineTitle()}
          indicator={toggleIndicatorIcon}
        >
          <HeroDivider className="my-3" />
          <Formik
            component={createLineForm}
            initialValues={initialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "materialName", type: "string", required: true },
              { name: "quantity", type: "string", required: true },
              ...orderLineFields
                .filter((field: iField) => field.name !== "materialName")
                .map((field) => ({
                  name: field.name,
                  type: "string" as const,
                  required: field.required,
                })),
            ])}
            onSubmit={handleSubmit}
            enableReinitialize
          />
        </AccordionItem>
      </Accordion>
    </Box>
  );
}
